import React from "react";
import CreationWizard from "../CreationWizard/CreationWizard";
import OnboardingModal from "../Onboarding/OnboardingModal";
import VideoPlayerModal from "../VideoPlayerModal";
import { DirectNoticeLauncher } from "../shared";
import { User } from "../../services/api";

export type DashboardModalsProps = {
  /** Auth context user (org admin / team) — passed through to OnboardingModal. */
  user: Record<string, unknown> | null | undefined;
  token: string | null | undefined;
  isOnboardingModalOpen: boolean;
  onOnboardingComplete: () => void;
  handleLogoFileUpload: (file: File) => Promise<boolean>;
  handleUpdateSchoolDescription: (description: string) => Promise<void>;
  handleBannerFileUpload: (base64Image: string) => Promise<boolean>;
  handleUpdatePhoneNumber: (phoneNumber: string) => Promise<void>;
  infoNoticeModalOpen: boolean;
  onCloseInfoNotice: () => void;
  sendImageModalOpen: boolean;
  onCloseSendImage: () => void;
  sendAttachmentModalOpen: boolean;
  onCloseSendAttachment: () => void;
  addEventModalOpen: boolean;
  selectedEventDate: Date | null;
  onCloseAddEvent: () => void;
  onNoticeDataRefresh: () => void;
  selectedVideo: { videoPath: string; title: string } | null;
  onCloseVideo: () => void;
  isDirectNoticeLauncherOpen: boolean;
  onCloseDirectNoticeLauncher: () => void;
  onSelectDirectNoticeUser: (
    selectedUser: User,
    mode: "direct_notice" | "dm_event"
  ) => void;
  directNoticeSelection: {
    user: User;
    mode: "direct_notice" | "dm_event";
  } | null;
  onCloseDirectNotice: () => void;
};

/**
 * Shared dashboard modals (notice creation wizards, onboarding, etc.).
 * Drive upload opens on `/drive` via `navigateToDriveUpload` — see `driveNavigation.ts`.
 * Keep wizard props in sync with Dashboard quick actions — notice creation is critical path.
 */
const DashboardModals: React.FC<DashboardModalsProps> = ({
  user,
  token,
  isOnboardingModalOpen,
  onOnboardingComplete,
  handleLogoFileUpload,
  handleUpdateSchoolDescription,
  handleBannerFileUpload,
  handleUpdatePhoneNumber,
  infoNoticeModalOpen,
  onCloseInfoNotice,
  sendImageModalOpen,
  onCloseSendImage,
  sendAttachmentModalOpen,
  onCloseSendAttachment,
  addEventModalOpen,
  selectedEventDate,
  onCloseAddEvent,
  onNoticeDataRefresh,
  selectedVideo,
  onCloseVideo,
  isDirectNoticeLauncherOpen,
  onCloseDirectNoticeLauncher,
  onSelectDirectNoticeUser,
  directNoticeSelection,
  onCloseDirectNotice,
}) => (
  <>
    {user && (
      <OnboardingModal
        open={isOnboardingModalOpen}
        onClose={onOnboardingComplete}
        user={user}
        token={token ?? null}
        onComplete={onOnboardingComplete}
        handleLogoFileUpload={handleLogoFileUpload}
        handleUpdateSchoolDescription={handleUpdateSchoolDescription}
        handleBannerFileUpload={handleBannerFileUpload}
        handleUpdatePhoneNumber={handleUpdatePhoneNumber}
      />
    )}

    {addEventModalOpen && (
      <CreationWizard
        noticeType="custom_event"
        open={addEventModalOpen}
        onClose={onCloseAddEvent}
        initialEventDate={selectedEventDate}
        onDataRefresh={onNoticeDataRefresh}
      />
    )}

    {infoNoticeModalOpen && (
      <CreationWizard
        noticeType="info"
        open={infoNoticeModalOpen}
        onClose={onCloseInfoNotice}
        onDataRefresh={onNoticeDataRefresh}
      />
    )}

    {sendImageModalOpen && (
      <CreationWizard
        noticeType="image_notice"
        open={sendImageModalOpen}
        onClose={onCloseSendImage}
        onDataRefresh={onNoticeDataRefresh}
      />
    )}

    {sendAttachmentModalOpen && (
      <CreationWizard
        noticeType="image"
        open={sendAttachmentModalOpen}
        onClose={onCloseSendAttachment}
        onDataRefresh={onNoticeDataRefresh}
      />
    )}

    <VideoPlayerModal
      open={selectedVideo !== null}
      onClose={onCloseVideo}
      videoPath={selectedVideo?.videoPath ?? ""}
      title={selectedVideo?.title ?? ""}
    />

    <DirectNoticeLauncher
      open={isDirectNoticeLauncherOpen}
      onClose={onCloseDirectNoticeLauncher}
      onSelectUser={onSelectDirectNoticeUser}
    />

    {directNoticeSelection && (
      <CreationWizard
        key={`${directNoticeSelection.mode}-${directNoticeSelection.user.id}`}
        noticeType={
          directNoticeSelection.mode === "dm_event" ? "custom_event" : "direct"
        }
        open
        wizardSubtitle={`Only ${directNoticeSelection.user.name} will see this message in the app.`}
        initialFormData={{ targetUserId: directNoticeSelection.user.id }}
        onClose={onCloseDirectNotice}
      />
    )}
  </>
);

export default DashboardModals;
